import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Dashboard from 'components/Dashboard/Dashboard.container';
import DashboardStore from 'components/Dashboard/Store';
import DashboardHeader from 'components/Dashboard/Header';
import { Products } from 'components/Dashboard/Store/Products/Products.container';
import { useStoreDetails } from 'api/store';
import { useOwnerDetails } from 'api/owners';
import { useSignedUser } from 'api/auth';
import { Spin } from 'antd';
import StoreWalkthrough from 'components/Onboarding/StoreWalkthrough';
import StoreReadyPanel from 'components/Onboarding/StoreReadyPanel';

const DashboardStorePage = () => {
  const params = useParams();

  const storeId = useMemo(() => (params.storeId ? Number(params.storeId) : null), [params]);
  const category = useMemo(() => (params.category ? params.category : 'all'), [params]);

  const { data: storeData, isLoading } = useStoreDetails(storeId, true);

  const { data: user } = useSignedUser();
  const { data: owner, invalidate: invalidateOwner } = useOwnerDetails(user?.owner_id);
  // Co-Hosts sign in as the Host's user, so this flow would read and write the Host's own
  // onboarding progress. The API rejects them outright, so keep it out of their view too.
  const isHost = !user?.is_cohost;
  const isReadyPanelOpen = Boolean(
    isHost && owner?.isFinishedOnboardingWalkthrough && !owner?.hasClosedOnboardingPanel,
  );

  if (!storeData || isLoading) {
    return <Spin spinning={isLoading} />;
  }

  return (
    <Dashboard page="products" storeId={storeId} doShowHeader>
      {isReadyPanelOpen && (
        <StoreReadyPanel
          store={storeData}
          walkthroughLastStep={owner?.onboardingWalkthroughStep ?? null}
          onClose={invalidateOwner}
        />
      )}
      <DashboardHeader store={storeData} />
      {isHost && <StoreWalkthrough storeId={storeId} onFinish={invalidateOwner} />}
      <DashboardStore
        page={category}
        storeId={storeId}
        hasClosedTutorial={storeData?.hasClosedTutorial}
      >
        {(products, newLocalProducts, isLoading, mutate, productsAreReordering) =>
          storeData && (
            <Products
              store={storeData}
              products={products}
              newLocalProducts={newLocalProducts}
              isLoading={isLoading}
              mutateProducts={mutate}
              productsAreReordering={productsAreReordering}
            />
          )
        }
      </DashboardStore>
    </Dashboard>
  );
};

export default DashboardStorePage;
